import React, { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import Checkbox from '@mui/material/Checkbox'
import { CircularProgress, Tooltip } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'

// Third-party Imports
import classnames from 'classnames'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table'

// Component Imports
import TablePaginationComponent from '../molecules/TablePaginationComponent'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

const httpPrefixer = (url) => {
  return url?.indexOf('://') === -1 ? `http://${url}` : url
}

// Column Definitions
const columnHelper = createColumnHelper()

const FirmListClientSide = (props) => {
  const {
    data,
    rowSelection,
    setRowSelection,
    setSelectedFirms,
    fetching,
    fuzzyFilter,
    globalFilter,
    setGlobalFilter,
    pageSize
  } = props
  const { t } = useTranslation()

  const skipPageResetRef = useRef()

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler()
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler()
            }}
          />
        )
      },
      columnHelper.accessor('name', {
        header: t('mailTemplate.wizard.firmList.firmName'),
        cell: ({ row }) => (
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <Typography color="text.primary" className="font-medium">
                {row.original.name}
              </Typography>
            </div>
          </div>
        )
      }),
      columnHelper.accessor('web', {
        header: t('mailTemplate.wizard.firmList.website'),
        cell: ({ row }) => (
          <div className="flex items-center gap-4">
            <div
              className="flex flex-col"
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <a
                href={httpPrefixer(row.original.web)}
                target="_blank"
                rel="noreferrer"
              >
                <Typography color="text.primary" className="font-medium">
                  {row.original.web}
                </Typography>
              </a>
            </div>
          </div>
        )
      }),
      columnHelper.accessor('emailList', {
        header: t('mailTemplate.wizard.firmList.email'),
        cell: ({ row }) => (
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <Typography color="text.primary" className="font-medium">
                {row.original.emailList?.length > 1
                  ? (
                  <Tooltip
                    title={
                      <div style={{ whiteSpace: 'pre-line' }}>
                        {row.original.emailList
                          .slice(1, row.original.emailList.length)
                          .join('\n')}
                      </div>
                    }
                    arrow
                  >
                    <span>{`${row.original.emailList[0]}...`}</span>
                  </Tooltip>
                    )
                  : (
                      row.original.emailList[0]
                    )}
              </Typography>
            </div>
          </div>
        )
      })
    ],
    [t]
  )

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter
    },
    state: {
      rowSelection,
      globalFilter
    },
    initialState: {
      pagination: {
        pageSize: 10
      }
    },
    enableRowSelection: true, // enable row selection for all rows
    // enableRowSelection: row => row.original.age > 18, // or enable row selection conditionally per row
    globalFilterFn: fuzzyFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: !skipPageResetRef.current,
    autoResetExpanded: !skipPageResetRef.current,
    getRowId: (row) => row.id
  })

  useEffect(() => {
    const selected = table
      .getSelectedRowModel()
      .rows.map(({ original }) => original)
    setSelectedFirms(selected)
  }, [rowSelection])

  useEffect(() => {
    // https://tanstack.com/table/latest/docs/faq#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
    skipPageResetRef.current = false
  })

  useEffect(() => {
    table.setPageSize(pageSize)
  }, [pageSize])

  return (
    <>
      <div className="overflow-x-auto">
        <table className={tableStyles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : (
                      <div
                        className={classnames({
                          'flex items-center': header.column.getIsSorted(),
                          'cursor-pointer select-none':
                            header.column.getCanSort()
                        })}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <ExpandLess />,
                          desc: <ExpandMore />
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {table.getFilteredRowModel().rows.length === 0
            ? (
            <tbody>
              <tr>
                <td
                  colSpan={table.getVisibleFlatColumns().length}
                  className="text-center"
                >
                  {fetching
                    ? (
                    <CircularProgress />
                      )
                    : (
                        t('mailTemplate.wizard.firmList.noData')
                      )}
                </td>
              </tr>
            </tbody>
              )
            : (
            <tbody>
              {table
                .getRowModel()
                .rows.slice(0, table.getState().pagination.pageSize)
                .map((row) => {
                  return (
                    <tr
                      key={row.id}
                      className={classnames({ selected: row.getIsSelected() })}
                      style={{ cursor: 'pointer' }}
                      onClick={row.getToggleSelectedHandler()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  )
                })}
            </tbody>
              )}
        </table>
      </div>
      <TablePagination
        component={() => (
          <TablePaginationComponent
            table={table}
            infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
            infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
          />
        )}
        count={table.getFilteredRowModel().rows.length}
        rowsPerPage={table.getState().pagination.pageSize}
        page={table.getState().pagination.pageIndex}
        onPageChange={(_, page) => {
          table.setPageIndex(page)
        }}
      />
    </>
  )
}

export default FirmListClientSide
